import {
  add, dec, div, gt, gte, isZero, lt, max, min, mul, pow, sub, ZERO, type Decimal,
} from '../core/decimal.ts';

export interface Insight {
  readonly text: string;
  /** null when the outcomes are roughly equal. */
  readonly isInvestBetter: boolean | null;
}

/** DebtVsInvestmentCalculator.getInsight: banded by spread = investmentReturn − debtInterestRate. */
export function debtVsInvestmentInsight(investmentReturn: Decimal, debtInterestRate: Decimal): Insight {
  const spread = sub(investmentReturn, debtInterestRate);
  if (gt(spread, dec(3))) {
    return {
      text: 'Strong case for investing while making minimum payments. Your expected returns significantly exceed your debt interest.',
      isInvestBetter: true,
    };
  }
  if (gt(spread, dec(1))) {
    return { text: 'Slight edge to investing. Consider your risk tolerance—investments aren\'t guaranteed.', isInvestBetter: true };
  }
  if (gt(spread, dec(-1))) {
    return { text: 'Roughly equal outcomes. Go with what helps you sleep at night.', isInvestBetter: null };
  }
  if (gt(spread, dec(-3))) {
    return {
      text: 'Slight edge to aggressive debt payoff. The guaranteed \'return\' of eliminating interest is valuable.',
      isInvestBetter: false,
    };
  }
  return {
    text: 'Strong case for aggressive debt payoff. Eliminating high-interest debt is your best investment.',
    isInvestBetter: false,
  };
}

export interface DebtVsInvestmentParams {
  readonly debtAmount: Decimal;
  readonly debtInterestRate: Decimal;
  readonly investmentReturn: Decimal;
  readonly monthlyPayment: Decimal;
  readonly totalMonthlyAllocation: Decimal;
  readonly years: number;
  readonly maxMonths: number;
}

export interface StrategyPoint {
  readonly month: number;
  readonly debt: Decimal;
  readonly investment: Decimal;
  readonly netWorth: Decimal;
  readonly totalInterestPaid: Decimal;
}

export interface DebtVsInvestmentResult {
  readonly aggressiveData: readonly StrategyPoint[];
  readonly investData: readonly StrategyPoint[];
  readonly aggressiveDebtFreeMonth: number;
  readonly investDebtFreeMonth: number;
  readonly crossoverMonth: number | null;
  readonly aggressiveTargetYear: StrategyPoint;
  readonly investTargetYear: StrategyPoint;
  readonly maxNetWorth: Decimal;
  readonly opportunityCost: Decimal;
  readonly timeDifference: number;
  readonly aggressiveMonthlyAmount: Decimal;
  readonly investMonthlyAmount: Decimal;
  readonly targetMonths: number;
  readonly aggressiveTotalInterestPaid: Decimal;
  readonly investTotalInterestPaid: Decimal;
  readonly winner: 'invest' | 'aggressive';
}

interface Rates {
  readonly debt: Decimal;
  readonly investGrowth: Decimal;
}

interface StrategyRun {
  readonly points: readonly StrategyPoint[];
  readonly debtFreeMonth: number;
}

/** One strategy's monthly split: the debt payment, and what is invested given the debt after paying. */
interface Allocation {
  readonly debtPayment: Decimal;
  readonly investsWhilePaying: boolean;
  readonly investedAmount: (debtAfterPayment: Decimal) => Decimal;
}

const point = (month: number, debt: Decimal, investment: Decimal, totalInterestPaid: Decimal): StrategyPoint => ({
  month,
  debt,
  investment,
  netWorth: sub(investment, debt),
  totalInterestPaid,
});

function runStrategy(params: DebtVsInvestmentParams, rates: Rates, allocation: Allocation): StrategyRun {
  const points: StrategyPoint[] = [point(0, params.debtAmount, ZERO, ZERO)];
  let debt = params.debtAmount;
  let investment: Decimal = ZERO;
  let interestPaid: Decimal = ZERO;
  let debtFreeMonth = 0;
  for (let month = 1; month <= params.maxMonths; month++) {
    const inDebt = gt(debt, ZERO);
    if (inDebt) {
      const interest = mul(debt, rates.debt);
      interestPaid = add(interestPaid, interest);
      debt = add(debt, interest);
      debt = max(sub(debt, min(allocation.debtPayment, debt)), ZERO);
      if (isZero(debt) && debtFreeMonth === 0) debtFreeMonth = month;
    }
    if (!inDebt || allocation.investsWhilePaying) {
      investment = add(mul(investment, rates.investGrowth), allocation.investedAmount(debt));
    }
    points.push(point(month, debt, investment, interestPaid));
  }
  return { points, debtFreeMonth };
}

/** First month aggressive is strictly ahead after having been strictly behind. */
function crossoverMonth(aggressive: readonly StrategyPoint[], invest: readonly StrategyPoint[]): number | null {
  let wasBehind = false;
  for (let i = 0; i < Math.min(aggressive.length, invest.length); i++) {
    if (lt(aggressive[i]!.netWorth, invest[i]!.netWorth)) wasBehind = true;
    if (wasBehind && gt(aggressive[i]!.netWorth, invest[i]!.netWorth)) return i;
  }
  return null;
}

/** Swift's variadic max: max(max(x, y), z), then any later value ≥ the running maximum. */
const swiftMaxOf = (x: Decimal, y: Decimal, z: Decimal, ...rest: readonly Decimal[]): Decimal => {
  const swiftMax = (a: Decimal, b: Decimal): Decimal => (gte(b, a) ? b : a);
  return rest.reduce((best, value) => (gte(value, best) ? value : best), swiftMax(swiftMax(x, y), z));
};

/**
 * DebtVsInvestmentCalculator.calculate: aggressive payoff (all allocation to debt, then all
 * invested) against minimum payment plus investing the rest, month by month for maxMonths
 * (≥ 1). Investments grow at the effective monthly rate (1 + r)^(1/12) − 1 via Double.
 */
export function calculateDebtVsInvestment(params: DebtVsInvestmentParams): DebtVsInvestmentResult {
  const monthlyInvestRate = sub(pow(add(dec(1), div(params.investmentReturn, dec(100))), div(dec(1), dec(12))), dec(1));
  const rates: Rates = {
    debt: div(div(params.debtInterestRate, dec(100)), dec(12)),
    investGrowth: add(dec(1), monthlyInvestRate),
  };
  const aggressivePayment = params.totalMonthlyAllocation;
  const investPayment = sub(params.totalMonthlyAllocation, params.monthlyPayment);

  const aggressive = runStrategy(params, rates, {
    debtPayment: aggressivePayment,
    investsWhilePaying: false,
    investedAmount: () => aggressivePayment,
  });
  const invest = runStrategy(params, rates, {
    debtPayment: params.monthlyPayment,
    investsWhilePaying: true,
    investedAmount: (debt) => (gt(debt, ZERO) ? investPayment : params.totalMonthlyAllocation),
  });

  const targetMonths = Math.max(0, params.years * 12);
  const atTarget = (points: readonly StrategyPoint[]): StrategyPoint => points[Math.min(targetMonths, points.length - 1)]!;
  const aggressiveTargetYear = atTarget(aggressive.points);
  const investTargetYear = atTarget(invest.points);
  const maxNetWorth = swiftMaxOf(
    aggressiveTargetYear.netWorth,
    investTargetYear.netWorth,
    aggressive.points.at(-1)!.netWorth,
    invest.points.at(-1)!.netWorth,
  );
  const opportunityCost = sub(investTargetYear.netWorth, aggressiveTargetYear.netWorth);

  return {
    aggressiveData: aggressive.points,
    investData: invest.points,
    aggressiveDebtFreeMonth: aggressive.debtFreeMonth,
    investDebtFreeMonth: invest.debtFreeMonth,
    crossoverMonth: crossoverMonth(aggressive.points, invest.points),
    aggressiveTargetYear,
    investTargetYear,
    maxNetWorth,
    opportunityCost,
    timeDifference: invest.debtFreeMonth - aggressive.debtFreeMonth,
    aggressiveMonthlyAmount: aggressivePayment,
    investMonthlyAmount: investPayment,
    targetMonths,
    aggressiveTotalInterestPaid: aggressive.points.at(-1)!.totalInterestPaid,
    investTotalInterestPaid: invest.points.at(-1)!.totalInterestPaid,
    winner: gt(opportunityCost, ZERO) ? 'invest' : 'aggressive',
  };
}
